<script setup lang="ts">
import BaseCheckbox from '@/components/BaseCheckbox.vue'
import BaseInput from '@/components/BaseInput.vue'
import BaseSelect from '@/components/BaseSelect.vue'
import { toast } from '@/composables/baseToast'
import { supabase } from '@/composables/supabaseClient'
import toastThrowError from '@/helpers/toastThrowError'
import { useDatabaseStore } from '@/stores/databaseStore'
import { useEstimateStore } from '@/stores/estimateStore'
import { ref, toRaw, watch } from 'vue'

const props = defineProps<{
  estimateId: string
}>()

const databaseStore = useDatabaseStore()
const estimateStore = useEstimateStore()
await estimateStore.loadEstimate(props.estimateId)
const estimate = ref(estimateStore.estimate)
watch(
  () => estimateStore.estimate,
  () => {
    estimate.value = toRaw(estimateStore.estimate)
  },
)

async function updateEstimate() {
  if (!estimate.value) throw toastThrowError('Estimate is not defined')
  const result = await supabase
    .from('estimate')
    .update({
      name: estimate.value.name!,
      estimate_type_id: estimate.value.estimate_type_id!,
      estimate_status_id: estimate.value.estimate_status_id!,
      is_template: estimate.value.is_template!,
      material_cost_markup: estimate.value.material_cost_markup,
      labor_cost_markup: estimate.value.labor_cost_markup,
      subcontractor_cost_markup: estimate.value.subcontractor_cost_markup,
      equipment_cost_markup: estimate.value.equipment_cost_markup,
      other_cost_markup: estimate.value.other_cost_markup,
      material_ohp_markup: estimate.value.material_ohp_markup,
      labor_ohp_markup: estimate.value.labor_ohp_markup,
      subcontractor_ohp_markup: estimate.value.subcontractor_ohp_markup,
      equipment_ohp_markup: estimate.value.equipment_ohp_markup,
      other_ohp_markup: estimate.value.other_ohp_markup,
    })
    .eq('id', estimate.value.id)
    .select()
    .single()
  if (result.error) throw toastThrowError('Unable to update estimate', result.error)
  toast('Updated summary', { type: 'success' })
}
</script>

<template>
  <div class="flex w-full flex-col overflow-auto p-3">
    <div v-if="estimate" class="grid w-full grid-cols-2 gap-3">
      <div class="flex flex-col gap-3">
        <h3 class="text-xl font-semibold text-gray-900">Details</h3>
        <BaseInput name="Code" v-model="estimate.code" disabled />
        <BaseInput name="Name" v-model="estimate.name" required />
        <BaseSelect
          name="Type"
          v-model="estimate.estimate_type_id"
          :select-options="databaseStore.estimateTypes"
          option-key="id"
          option-name="name"
          option-value="id"
        />
        <BaseSelect
          name="Status"
          v-model="estimate.estimate_status_id"
          :select-options="databaseStore.estimateStatus"
          option-key="id"
          option-name="name"
          option-value="id"
        />
        <BaseSelect
          name="Cost Source"
          v-model="estimate.cost_source_id"
          disabled
          :select-options="databaseStore.costSources"
          option-key="id"
          option-name="name"
          option-value="id"
        />
        <BaseCheckbox v-model="estimate.is_template" name="Save as Template" />
      </div>
      <div class="flex flex-col gap-3 overflow-auto">
        <h3 class="text-xl font-semibold text-gray-900">Markups</h3>
        <div class="grid grid-cols-2 gap-3">
          <div class="flex flex-col gap-3">
            <BaseInput
              name="Material Direct Markup"
              v-model="estimate.material_cost_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Labor Direct Markup"
              v-model="estimate.labor_cost_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Sub Direct Markup"
              v-model="estimate.subcontractor_cost_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Equipment Direct Markup"
              v-model="estimate.equipment_cost_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Other Direct Markup"
              v-model="estimate.other_cost_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
          </div>
          <div class="flex flex-col gap-3">
            <BaseInput
              name="Material OH&P Markup"
              v-model="estimate.material_ohp_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Labor OH&P Markup"
              v-model="estimate.labor_ohp_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Sub OH&P Markup"
              v-model="estimate.subcontractor_ohp_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Equipment OH&P Markup"
              v-model="estimate.equipment_ohp_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
            <BaseInput
              name="Other OH&P Markup"
              v-model="estimate.other_ohp_markup"
              type="number"
              min="0"
              max="1"
              step="0.01"
              :formatter="(value) => `${((value ?? 0) * 100).toFixed(2)}%`"
              :converter="(value) => (value ? value / 100 : undefined)"
            />
          </div>
        </div>
      </div>
    </div>
    <div v-else>
      <span>Loading Estimate</span>
    </div>
    <button
      class="me-2 mb-2 cursor-pointer rounded-lg bg-blue-700 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-800 focus:ring-4 focus:ring-blue-300"
      @click="updateEstimate()"
    >
      Save
    </button>
  </div>
</template>
